import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const DEFAULT_CENTER = [55.751244, 37.618423];

const getInfrastructureStatusLabel = (infra) => {
  if (infra.status === 'checking') return 'Проверяется';
  if (infra.status === 'found') return 'Рядом найден объект';
  if (infra.status === 'not_found') return 'Объектов рядом нет';
  if (infra.status === 'error') return 'Ошибка проверки';
  return 'Ожидание проверки';
};

const getMarkerStyle = (flags) => {
  if (flags?.proximityViolation) {
    return {
      color: '#b71c1c',
      fillColor: '#ef5350'
    };
  }

  return {
    color: '#0d47a1',
    fillColor: '#42a5f5'
  };
};

const PointMap = ({ points, pointFlags, pairs, onDeletePoint, showDistanceGrid = true }) => {
  const rootRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    const container = rootRef.current;
    if (!container) return;

    if (container._leaflet_id) {
      delete container._leaflet_id;
    }

    const map = L.map(container, { preferCanvas: true, zoomControl: true }).setView(DEFAULT_CENTER, 10);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);

    const layer = L.layerGroup().addTo(map);

    mapRef.current = map;
    layerRef.current = layer;

    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
      mapRef.current = null;
      layerRef.current = null;
      if (container._leaflet_id) {
        delete container._leaflet_id;
      }
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();

    const drawLayer = L.layerGroup().addTo(layer);
    const labelLayer = L.layerGroup().addTo(layer);

    if (showDistanceGrid) {
      pairs.forEach((pair) => {
        const isViolation = pair.distance < 30;
        const latLngs = [
          [pair.first.latitude, pair.first.longitude],
          [pair.second.latitude, pair.second.longitude]
        ];

        L.polyline(latLngs, {
          color: isViolation ? '#ff1744' : '#1976d2',
          weight: isViolation ? 2.5 : 1.2,
          opacity: isViolation ? 0.85 : 0.45,
          dashArray: isViolation ? null : '5, 7'
        }).addTo(drawLayer);

        const midLat = (pair.first.latitude + pair.second.latitude) / 2;
        const midLng = (pair.first.longitude + pair.second.longitude) / 2;
        const distanceLabel = pair.distance < 1000
          ? `${Math.round(pair.distance)} м`
          : `${(pair.distance / 1000).toFixed(1)} км`;

        const labelIcon = L.divIcon({
          className: '',
          html: `<span style="font-size:8px;font-weight:600;color:${isViolation ? '#b71c1c' : '#0d47a1'};background:rgba(255,255,255,0.75);padding:0 2px;border-radius:2px;white-space:nowrap;box-shadow:0 0 1px rgba(0,0,0,0.2);display:inline-block;transform:translate(-50%,-50%);">${distanceLabel}</span>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0]
        });

        L.marker([midLat, midLng], { icon: labelIcon, interactive: false, keyboard: false, zIndexOffset: -1000 })
          .addTo(labelLayer);
      });
    }

    points.forEach((point) => {
      const flags = pointFlags[point.pointKey] || {
        proximityViolation: false,
        infrastructureViolation: false
      };

      if (flags.infrastructureViolation) {
        L.circle([point.latitude, point.longitude], {
          radius: 20,
          color: '#ff1744',
          fillColor: '#ff1744',
          fillOpacity: 0.15,
          weight: 1.5
        }).addTo(layer);
      }

      const markerStyle = getMarkerStyle(flags);
      const marker = L.circleMarker([point.latitude, point.longitude], {
        radius: 9,
        color: markerStyle.color,
        fillColor: markerStyle.fillColor,
        fillOpacity: 0.95,
        weight: 2
      }).addTo(layer);

      const infra = point.infrastructure;
      marker.bindPopup(`
        <div style="min-width: 200px;">
          <div style="font-weight: 700; margin-bottom: 6px;">${point.name}</div>
          <div style="font-size: 12px; margin-bottom: 6px;">${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}</div>
          <div style="font-size: 12px; margin-bottom: 4px;">30м правило: ${flags.proximityViolation ? 'Нарушение' : 'ОК'}</div>
          <div style="font-size: 12px; margin-bottom: 4px;">Инфраструктура: ${getInfrastructureStatusLabel(infra)}</div>
          ${infra.status === 'found' ? `<div style=\"font-size: 12px;\">${infra.amenity || 'Объект'}: ${infra.distanceMeters.toFixed(1)} м</div>` : ''}
          ${infra.status === 'error' ? `<div style=\"font-size: 12px; color: #d32f2f;\">${infra.errorMessage || 'Ошибка проверки'}</div>` : ''}
          <button
            type="button"
            data-delete-point="1"
            style="margin-top:8px;padding:4px 8px;border:1px solid #d32f2f;background:#fff;color:#d32f2f;border-radius:4px;cursor:pointer;font-size:12px;"
          >
            Удалить точку
          </button>
        </div>
      `);

      marker.on('popupopen', (event) => {
        const popupElement = event.popup?.getElement();
        if (!popupElement) return;

        const deleteButton = popupElement.querySelector('[data-delete-point="1"]');
        if (!deleteButton) return;

        deleteButton.onclick = () => {
          if (typeof onDeletePoint !== 'function') return;

          const confirmed = window.confirm(`Удалить точку "${point.name}"?`);
          if (!confirmed) return;

          Promise.resolve(onDeletePoint(point.pointKey)).finally(() => {
            map.closePopup();
          });
        };
      });
    });

    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map((p) => [p.latitude, p.longitude]));
      if (bounds.isValid()) {
        map.fitBounds(bounds.pad(0.05), { maxZoom: 18 });
      }
    } else {
      map.setView(DEFAULT_CENTER, 14);
    }
  }, [onDeletePoint, pairs, pointFlags, points, showDistanceGrid]);

  return (
    <Box
      ref={rootRef}
      sx={{
        height: {
          xs: 520,
          md: 760
        },
        width: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: '#f3f6fa'
      }}
    />
  );
};

export default PointMap;
